/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import {
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

import { isDistinct } from 'ng/shared/utils/ngrx-store.utils';
import { Timeframe } from 'ajs/js/services/Timeframe';
import { AviDatePipe } from 'ng/modules/core/pipes/avi-date.pipe';
import { addPoolIdFilterParams, getVsConfigForTrafficLogs } from 'ng/modules/logs/utils/logs.utils';
import { LogsApiRequestService } from '../../services/logs-api-request.service';

import {
    LogsType,
    TLogEntrySignatureParams,
    TLogListStateParams,
    TLogsGraphStateParams,
    TLogsPageStateParams,
    TLogStateParams,
    VsAppProfileType,
} from '../../logs.types';

import { convertLogEntryParamsToQueryStrings } from '../../utils/logs-filters.utils';

import {
    ILogsStates,
    IVsConfigForLogs,
    LogsStore,
    TTableProps,
} from '../../services/logs.store';
import { TTrafficLogIdentifier } from './traffic-logs.types';

/**
 * A type that extends the IVsConfigForLogs type with additional properties specific to traffic logs.
 * This type is used to manage the configuration of virtual services for traffic logs.
 * @param defaultVsIp - the default IP address of the VS to display when vs_ip and vs_ip6 are both not available.
 * @param hasOverrideL4AppProfile - flag to indicate if VS has an override application profile.
 * @param nonSignificantLogsEnabled - flag to indicate whether the VS enables non-significant logs.
 * @param hasWafPolicy - whether the VS has WAF logs or not.
 * @param wafPolicyRef - the reference to the WAF policy.
 * @param hasTcpProxyNetProfile - whether the VS has a TCP proxy net profile.
 */
export interface IVsConfigForTrafficLogs extends IVsConfigForLogs {
    defaultVsIp?: string;
    hasOverrideL4AppProfile?: boolean;
    nonSignificantLogsEnabled?: boolean;
    hasWafPolicy?: boolean;
    wafPolicyRef?: string;
    hasTcpProxyNetProfile?: boolean; // for L4 use only
}

type TTrafficLogTableProps = TTableProps & {
    selectedLogs: TTrafficLogIdentifier[];
};

export interface ITrafficLogsStates extends ILogsStates {
    vsConfig?: IVsConfigForTrafficLogs;
    // override the generic TTableProps type to include a more specific string type of log identifiers
    tableProps?: TTrafficLogTableProps;
}

/**
 * @abstract
 * @description
 *     An abstract store class that manages states of a traffic logs (Application + Connection logs) dedicated module.
 *     This store works as a middle layer between a concrete injectable end-point store and the more abstract LogStore.
 *     The service instances passed to the constructor should be from a child concrete store's injected services.
 * @author Zhiqian Liu
 */
export abstract class TrafficLogsStore<T extends ITrafficLogsStates> extends LogsStore<T> {
    // ********************************** Selectors **********************************e

    public poolUuid$?: Observable<string>;

    public override selectedLogs$: Observable<TTrafficLogIdentifier[]> = this.select(
        state => state.tableProps?.selectedLogs,
    );

    /**
     * Selector returning params needed for traffic log list components.
     */
    public override logListApiParams$: Observable<TLogListStateParams> = this.select(
        state => {
            const {
                adf,
                duration,
                end,
                page,
                page_size: pageSize,
                udf,
                nf,
                type,
                virtualservice,
                filters,
                orderby,
            } = state.apiParams;

            return {
                adf,
                duration: state.tableProps?.customDuration || duration,
                end: state.tableProps?.customEndTime || end,
                page,
                page_size: pageSize,
                udf,
                nf,
                type,
                virtualservice,
                filters,
                orderby,
            };
        },
    ).pipe(
        isDistinct(),
    );

    /**
     * Selector returning params needed for fetching individual traffic logs.
     */
    public override logApiParams$: Observable<TLogStateParams> = this.select(state => {
        const {
            adf,
            duration,
            end,
            udf,
            nf,
            type,
            virtualservice,
        } = state.apiParams;

        return {
            adf,
            duration,
            end,
            udf,
            nf,
            type,
            virtualservice,
        };
    })
        .pipe(
            isDistinct(),
        );

    /**
     * Selector returning params needed for traffic log graph components.
     */
    public override logsGraphApiParams$: Observable<TLogsGraphStateParams> = this.select(
        state => {
            // TODO 150205 modify accordingly based on findings about page_size request param;
            // must wait for backend to resolve
            const {
                adf,
                duration,
                end,
                nf,
                step,
                udf,
                type,
                virtualservice,
                filters,
            } = state.apiParams;

            return {
                adf,
                duration,
                end,
                nf,
                // TODO Remove page_size after 150205
                page_size: 10000,
                step,
                udf,
                type,
                virtualservice,
                filters,
            };
        },
    ).pipe(
        isDistinct(),
    );

    /**
     * Selector returning params reflecting overall page params for traffic logs.
     */
    public override logsPageParams$: Observable<TLogsPageStateParams> = this.select(
        state => {
            const {
                adf,
                duration,
                end,
                nf,
                udf,
                type,
                virtualservice,
                filters,
            } = state.apiParams;

            return {
                adf,
                duration,
                end,
                nf,
                udf,
                type,
                virtualservice,
                filters,
            };
        },
    ).pipe(
        isDistinct(),
    );

    public readonly nonSignificantLogsEnabled$ = this.select(
        state => state.vsConfig.nonSignificantLogsEnabled,
    );

    public readonly hasOverrideL4AppProfile$ = this.select(
        state => state.vsConfig.hasOverrideL4AppProfile,
    );

    public readonly defaultVsIp$ = this.select(state => state.vsConfig.defaultVsIp);

    public readonly hasWafPolicy$ = this.select(state => state.vsConfig.hasWafPolicy);

    public readonly wafPolicyRef$ = this.select(state => state.vsConfig.wafPolicyRef);

    public readonly hasTcpProxyNetProfile$ = this.select(
        ({ vsConfig: { hasTcpProxyNetProfile } }) => hasTcpProxyNetProfile,
    );

    // ****************************** Effects **********************************

    /**
     * @override
     * Download all logs with the current set of parameters.
     */
    public readonly downloadAllLogs = this.effect(downloadAllLogs$ => downloadAllLogs$.pipe(
        withLatestFrom(this.logsPageParams$),
        switchMap(([_, apiParams]: [void, TLogsPageStateParams]) => {
            if (this.poolUuid$) {
                return this.poolUuid$.pipe(
                    take(1),
                    map(poolUuid => addPoolIdFilterParams({ ...apiParams }, poolUuid)),
                );
            }

            return of(apiParams);
        }),
        tap((finalApiParams: TLogsPageStateParams) => {
            LogsApiRequestService.downloadLogs(finalApiParams);
        }),
    ));

    /**
     * @override
     * Download one log entry, given log ID, service engine ID, and VCPU ID and timestamp .
     */
    public downloadLogEntry = this.effect<TLogEntrySignatureParams>(
        logEntryParams$ => logEntryParams$.pipe(
            withLatestFrom(this.logApiParams$),
            tap(([logEntryParams, apiParams]: [TLogEntrySignatureParams, TLogStateParams]) => {
                const newApiParams: TLogStateParams = {
                    ...apiParams,
                    filters: convertLogEntryParamsToQueryStrings(logEntryParams),
                };

                LogsApiRequestService.downloadLogs(newApiParams);
            }),
        ),
    );

    // **************************** Constructor ******************************

    constructor(
        initialState: T,
        protected readonly timeframe: Timeframe,
        protected readonly aviDatePipe: AviDatePipe,
        protected readonly store: Store,
    ) {
        super(initialState, timeframe, aviDatePipe, store);
    }

    // **************************** Updaters ******************************

    /**
     * @override
     * Update state with new VS configs and VS related API params based on a virtual service item.
     * TODO: change "any" to the VirtualService class once it's been converted to Typescript.
     */
    public setVsProps(virtualService: any): void {
        const vsConfig = getVsConfigForTrafficLogs(virtualService);

        const {
            logsType,
            nonSignificantLogsEnabled,
            vsUuid,
        } = vsConfig;

        this.setState(state => ({
            ...state,
            apiParams: {
                ...state.apiParams,
                type: logsType,
                udf: nonSignificantLogsEnabled ? state.apiParams.udf : false,
                nf: nonSignificantLogsEnabled ? state.apiParams.nf : false,
                virtualservice: vsUuid,
            },
            vsConfig,
        }));
    }

    /**
     * Update the VS App Profile Type.
     * Applies only to VS's that have multiple App profile types.
     */
    public setVsAppProfileType(vsAppProfileType: VsAppProfileType): void {
        // type is set to 1 for L7 and 0 for L4 and DNS
        const vsLogsType = vsAppProfileType === VsAppProfileType.L7 ?
            LogsType.APPLICATION :
            LogsType.CONNECTION;

        this.setState(state => ({
            ...state,
            apiParams: {
                ...state.apiParams,
                type: LogsType,
            },
            vsConfig: {
                ...state.vsConfig,
                vsAppProfileType,
                vsLogsType,
            },
        }));
    }

    /**
     * Update API params for significant logs.
     */
    public setSignificantParams(value: boolean): void {
        this.setState(state => ({
            ...state,
            apiParams: {
                ...state.apiParams,
                adf: value,
            },
        }));
    }

    /**
     * Update API params for non-significant logs.
     */
    public setNonSignificantParams(value: boolean): void {
        this.setState(state => ({
            ...state,
            apiParams: {
                ...state.apiParams,
                nf: value,
            },
        }));
    }

    /**
     * Update API params for user defined logs.
     */
    public setUserDefinedParams(value: boolean): void {
        this.setState(state => ({
            ...state,
            apiParams: {
                ...state.apiParams,
                udf: value,
            },
        }));
    }
}
